import React, { useState } from 'react';
import { Clock, Search, ArrowUp, Shield, Lock, Server, Key } from 'lucide-react';

const DataProtection: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Handle scroll events for back-to-top button
  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="container mx-auto px-4 md:px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-['Poppins']">
            Data Protection
          </h1>
          
          <div className="flex items-center gap-4 text-gray-600 mb-8">
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              <span>7 min read</span>
            </div>
            <div className="h-1 w-1 bg-gray-300 rounded-full"></div>
            <span>Last updated: March 15, 2025</span>
          </div>

          {/* Search */}
          <div className="relative mb-12">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search data protection policy..."
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#C2A4D6] focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Security Measures */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Shield className="w-8 h-8 text-[#C2A4D6] mr-3" />
              <h2 className="text-2xl font-semibold">Security Measures</h2>
            </div>
            <p className="text-gray-600 mb-6">
              We implement industry-leading security measures to protect your data:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              <li>End-to-end encryption for all conversations</li>
              <li>Regular security audits and penetration testing</li>
              <li>Advanced threat detection and prevention systems</li>
              <li>Continuous monitoring for suspicious activities</li>
            </ul>
          </section>

          {/* Data Encryption */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Lock className="w-8 h-8 text-[#C2A4D6] mr-3" />
              <h2 className="text-2xl font-semibold">Data Encryption</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Your data is protected using state-of-the-art encryption:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              <li>AES-256 encryption for data at rest</li>
              <li>TLS 1.3 for all data in transit</li>
              <li>Secure key management and rotation</li>
              <li>Encrypted backups with separate key storage</li>
            </ul>
          </section>

          {/* Data Storage */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Server className="w-8 h-8 text-[#C2A4D6] mr-3" />
              <h2 className="text-2xl font-semibold">Data Storage</h2>
            </div>
            <p className="text-gray-600 mb-6">
              We maintain strict controls over data storage and access:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              <li>Secure cloud infrastructure with redundancy</li>
              <li>Geographic data replication for reliability</li>
              <li>Regular backup procedures and testing</li>
              <li>Strict access controls and logging</li>
            </ul>
          </section>

          {/* Access Controls */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Key className="w-8 h-8 text-[#C2A4D6] mr-3" />
              <h2 className="text-2xl font-semibold">Access Controls</h2>
            </div>
            <p className="text-gray-600 mb-6">
              We implement strict access controls to protect your information:
            </p>
            <ul className="list-disc pl-5 space-y-3 text-gray-600">
              <li>Role-based access control (RBAC)</li>
              <li>Regular access reviews and audits</li>
              <li>Automated suspicious activity detection</li>
              <li>Detailed access logging and monitoring</li>
            </ul>
          </section>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-[#C2A4D6] hover:bg-[#B390C9] text-white p-3 rounded-full shadow-lg transition-colors"
          aria-label="Back to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default DataProtection;